/* eslint-disable spire/export-styles */
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setMinimumQtyError from "@tKgpco/Store/Data/ProductMultipleQty/Handlers/setProductMinimunQtyErrorHandler";
import setMultipleQtyError from "@tKgpco/Store/Data/ProductMultipleQty/Handlers/setProductMultpleSalesQtyErrorHandler";
import { COLORS } from "@tKgpco/theme/design";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
}

const mapStateToProps = (
    state: CustomApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        hasMinimumQtyError: state.KGPState.ProductMultipleQtyStore.showMinimumQtyError,
        hasMultipleSalesQtyError: state.KGPState.ProductMultipleQtyStore.showMultipleQtyError,
    };
};

const mapDispatchToProps = {
    setMinimumQtyError,
    setMultipleQtyError,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps>;

export interface LocalStyles {
    errMsg?: InjectableCss;
}

const localStyles: LocalStyles = {
    errMsg: {
        css: css`
            font-size: 15px;
            font-weight: bold;
            color: #fc3939;
        `,
    },
};

const myStyles = localStyles;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
            text-align: center;
            background-color: #f1f6fb;
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        product: { id: productId, minimumOrderQty, detail },
        onQtyOrderedChanged,
        productInfo: { qtyOrdered, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    variantSelectionCompleted,
    setMinimumQtyError,
    setMultipleQtyError,
    hasMinimumQtyError,
    hasMultipleSalesQtyError,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    const [productQty, setProductQty] = useState(qtyOrdered.toString());
    const multipleSaleQty = Number(detail?.multipleSaleQty);
    useEffect(() => {
        setMinimumQtyError({
            setMinimumQtyError: minimumOrderQty > 1 && qtyOrdered < minimumOrderQty,
        });

        setMultipleQtyError({
            setMultipleQtyError:
                !(qtyOrdered <= minimumOrderQty) && multipleSaleQty > 1 && qtyOrdered % multipleSaleQty !== 0,
        });

        if (qtyOrdered === Number(productQty)) {
            return;
        }
        setProductQty(`${qtyOrdered || ""}`);
    }, [qtyOrdered]);

    if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);

        const possibleNumber = Number(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        if (!Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    return (
        <StyledWrapper
            style={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
            }}
        >
            <TextField
                label={labelOverride ?? translate("QTY")}
                value={productQty}
                type="number"
                min={0}
                max={999999}
                data-test-selector="product_qtyOrdered"
                {...otherProps}
                onKeyPress={keyPressHandler}
                onChange={qtyChangeHandler}
                {...styles}
            />
            {minimumOrderQty > 1 && hasMinimumQtyError && (
                <StyledWrapper {...myStyles.errMsg}>{siteMessage("min_qty_msg")}</StyledWrapper>
            )}
            {multipleSaleQty > 1 && hasMultipleSalesQtyError && (
                <StyledWrapper {...myStyles.errMsg}>{siteMessage("multiple_qty_msg")}</StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductQuantityOrdered));
